/**
 * Portal Progress Calculation Utility (Candidate-owned)
 *
 * This is the progress the candidate should see in the portal: it credits the candidate
 * for submitting portal items, independent of 242-team follow-up work.
 *
 * Steps (10):
 * 1. Profile
 * 2. Why Church Planting
 * 3. References submitted
 * 4. Sending Church
 * 5. Background check submitted
 * 6. Prepare/Enrich requested (married) OR explicitly skipped (not married)
 * 7. Spouse Interview submitted (married) OR explicitly skipped (not married)
 * 8. Theology
 * 9. Spiritual health surveys submitted (candidate-only if single; both if married)
 * 10. Ministry Transitions
 */

export interface PortalCandidateForProgress {
  profileCompleted?: boolean;
  whyChurchPlantingCompleted?: boolean;
  theologyCompleted?: boolean;
  referencesCompleted?: boolean;
  sendingChurchCompleted?: boolean;
  maritalStatus?: string;
  prepareEnrichStatus?: string;
  prepareEnrichRequested?: boolean;
  prepareEnrichData?: any;
  spouseInterviewSubmitted?: boolean;
  spouseInterviewData?: any;
  spouseInterviewStatus?: string;
  backgroundCheckStatus?: string;
  spiritualHealthCompleted?: boolean;
  spiritualHealthCandidateSubmitted?: boolean;
  spiritualHealthSpouseSubmitted?: boolean;
  ministryTransitionsCompleted?: boolean;
}

export interface ReferenceForProgress {
  contactedByHub?: boolean;
}

/**
 * Calculate progress percentage from onboarding status steps (for OnboardingWizard)
 */
export function calculateProgressFromSteps(
  steps: {
    profile?: boolean;
    whyChurchPlanting?: boolean;
    theology?: boolean;
    references?: boolean;
    sendingChurch?: boolean;
    backgroundCheck?: boolean;
    prepareEnrich?: boolean;
    spouseInterview?: boolean;
    spiritualHealth?: boolean;
    ministryTransitions?: boolean;
  },
  profileData?: {
    maritalStatus?: string;
    prepareEnrichStatus?: string;
  }
): number {
  let completedSteps = 0;
  const totalSteps = 10;

  // Step 1: Profile
  if (steps.profile) completedSteps++;

  // Step 2: Why Church Planting
  if (steps.whyChurchPlanting) completedSteps++;

  // Step 3: References (simplified - full credit if completed)
  if (steps.references) completedSteps++;

  // Step 4: Sending Church
  if (steps.sendingChurch) completedSteps++;

  // Step 5: Background Check
  if (steps.backgroundCheck) completedSteps++;

  // Step 6: Prepare/Enrich
  if (profileData?.maritalStatus !== 'married') {
    if (steps.prepareEnrich) {
      completedSteps++;
    }
  } else if (steps.prepareEnrich) {
    completedSteps++;
  }

  // Step 7: Spouse Interview
  if (profileData?.maritalStatus !== 'married') {
    if (steps.spouseInterview) {
      completedSteps++;
    }
  } else if (steps.spouseInterview) {
    completedSteps++;
  }

  // Step 8: Theology
  if (steps.theology) completedSteps++;

  // Step 9: Spiritual Health
  if (steps.spiritualHealth) completedSteps++;

  // Step 10: Ministry Transitions
  if (steps.ministryTransitions) completedSteps++;

  return Math.round((completedSteps / totalSteps) * 100);
}

/**
 * Calculate progress percentage from full candidate object (matches backend logic exactly)
 */
export function calculateProgressPercentage(
  candidate: PortalCandidateForProgress,
  references: ReferenceForProgress[] = []
): number {
  let completedSteps = 0;
  const totalSteps = 10; // 10 main steps (excluding completion step)
  
  // Step 1: Profile
  if (candidate.profileCompleted) completedSteps++;

  // Step 2: Why Church Planting
  if (candidate.whyChurchPlantingCompleted) completedSteps++;
  
  // Step 3: References (candidate-owned): submitted = complete
  if (candidate.referencesCompleted) completedSteps++;
  
  // Step 4: Sending Church
  if (candidate.sendingChurchCompleted) completedSteps++;
  
  // Step 5: Background Check (candidate-owned): submitted = complete
  if (candidate.backgroundCheckStatus === 'submitted' || candidate.backgroundCheckStatus === 'received') completedSteps++;
  
  // Step 6: Prepare/Enrich (candidate-owned): requested (married) or skipped (not married)
  const isMarried = candidate.maritalStatus === 'married';
  const prepareEnrichSkipped = candidate.prepareEnrichData?.skipped === true;
  if (isMarried) {
    if (candidate.prepareEnrichRequested || candidate.prepareEnrichStatus === 'started' || candidate.prepareEnrichStatus === 'scheduled' || candidate.prepareEnrichStatus === 'complete') {
      completedSteps++;
    }
  } else {
    if (prepareEnrichSkipped) completedSteps++;
  }

  // Step 7: Spouse Interview (candidate-owned): submitted (married) or skipped (not married)
  const spouseInterviewSkipped = candidate.spouseInterviewData?.skipped === true;
  if (isMarried) {
    if (candidate.spouseInterviewSubmitted) completedSteps++;
  } else {
    if (spouseInterviewSkipped) completedSteps++;
  }

  // Step 8: Theology
  if (candidate.theologyCompleted) completedSteps++;
  
  // Step 9: Spiritual Health (candidate-owned): submitted surveys = complete
  if (!isMarried) {
    if (candidate.spiritualHealthCandidateSubmitted) completedSteps++;
  } else {
    if (candidate.spiritualHealthCandidateSubmitted && candidate.spiritualHealthSpouseSubmitted) completedSteps++;
  }

  // Step 10: Ministry Transitions
  if (candidate.ministryTransitionsCompleted) completedSteps++;
  
  return Math.round((completedSteps / totalSteps) * 100);
}

export default calculateProgressPercentage;
